// src/components/ScreenshotUpload/ScreenshotUpload.tsx
import React, { useEffect, useCallback } from 'react';
import { message } from 'antd';
import { useTranslation } from 'react-i18next';
import API from '../../services/ApiService';
import { buildPhotoFormData, validatePhotoFile } from '../../utils/photoUpload';

interface ScreenshotUploadProps {
  wordsListId?: string;
  // `assetId` to wiersz w `image_assets` założony przez upload — słowo musi go dostać,
  // inaczej własne zdjęcia nie mają powiązania, po którym faza 3 będzie czytać obrazki
  onImageUploaded?: (url: string, assetId?: number) => void;
  // Zamiast wysyłać, oddaje sprawdzony plik rodzicowi. Dla ekranów, na których wklejony obraz jest
  // dopiero kandydatem (katalog) — na serwer idzie wtedy, gdy rodzic uzna go za wybrany.
  onFileAccepted?: (file: File) => void;
  isActive: boolean;
}

export const ScreenshotUpload: React.FC<ScreenshotUploadProps> = ({
  wordsListId,
  onImageUploaded,
  onFileAccepted,
  isActive,
}) => {
  const { t } = useTranslation();
  const uploadScreenshot = useCallback(async (blob: Blob | File) => {
    const file = new File([blob], `screenshot-${Date.now()}.png`, {
      type: blob.type || 'image/png',
    });

    // Te same reguły co przy wyborze pliku z dysku — inaczej za duży zrzut wraca
    // z serwera jako generyczny błąd, bez informacji, co było nie tak
    const validationError = validatePhotoFile(file);
    if (validationError) {
      message.error(validationError);

      return;
    }

    // Podgląd u rodzica wystarczy za potwierdzenie — komunikat „dodany" byłby tu nieprawdą
    if (onFileAccepted) {
      onFileAccepted(file);

      return;
    }

    const loadingMessage = message.loading(t('words-list:screenshot.uploading'), 0);

    try {
      const formData = buildPhotoFormData(file, wordsListId);

      const response = await API.photos.add(formData);
      onImageUploaded?.(response.data.url, response.data.assetId);
      message.success(t('words-list:screenshot.uploaded'));
    } catch (error) {
      message.error(t('words-list:screenshot.uploadError'));
      console.error(error);
    } finally {
      loadingMessage();
    }
  }, [wordsListId, onImageUploaded, onFileAccepted, t]);

  // Paste ze schowka (Ctrl+V)
  const handlePaste = useCallback(async (e: ClipboardEvent) => {
    if (!isActive) return;

    const items = e.clipboardData?.items;
    if (!items) return;

    const imageItems = Array.from(items).filter((item) => item.type.startsWith('image/'));

    if (imageItems.length > 0) {
      e.preventDefault();
      const blob = imageItems[0].getAsFile();
      if (blob) {
        await uploadScreenshot(blob);
      }
    }
  }, [isActive, uploadScreenshot]);

  // Drag & Drop
  const handleDrop = useCallback(async (e: DragEvent) => {
    if (!isActive) return;
    e.preventDefault();

    const files = e.dataTransfer?.files;
    if (!files) return;

    const imageFiles = Array.from(files).filter((file) => file.type.startsWith('image/'));

    if (imageFiles.length > 0) {
      await uploadScreenshot(imageFiles[0]);
    }
  }, [isActive, uploadScreenshot]);

  const handleDragOver = useCallback((e: DragEvent) => {
    e.preventDefault();
  }, []);

  useEffect(() => {
    if (!isActive) return undefined;

    document.addEventListener('paste', handlePaste);
    document.addEventListener('dragover', handleDragOver);
    document.addEventListener('drop', handleDrop);

    return () => {
      document.removeEventListener('paste', handlePaste);
      document.removeEventListener('dragover', handleDragOver);
      document.removeEventListener('drop', handleDrop);
    };
  }, [isActive, handlePaste, handleDrop, handleDragOver]);

  return null;
};

export default ScreenshotUpload;
